import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { SocialLoginButtons } from '../components/SocialLoginButtons';
import { toast } from 'sonner';
import { Eye, EyeOff, ArrowRight, ArrowUpRight } from 'lucide-react';
import { MagneticButton } from '../components/MagneticButton';

// Palette — 3 colors only:
//   #0B0C10 obsidian, #F8F9FA paper, #E3A869 brass
const BRASS = '#E3A869';

const stagger = { hidden: { opacity: 1 }, show: { transition: { staggerChildren: 0.07, delayChildren: 0.12 } } };
const fadeUp = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };

export const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Treat 502/503/504/network errors as "backend warming up" and retry once
  // before showing a hard error. Genuine bad-credentials (401) fail fast.
  const attemptLogin = async () => {
    try {
      await login(email, password);
      return { ok: true };
    } catch (err) {
      const status = err?.response?.status;
      const isTransient = !err?.response || status === 502 || status === 503 || status === 504;
      return { ok: false, status, isTransient, detail: err?.response?.data?.detail, err };
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let result = await attemptLogin();
      if (!result.ok && result.isTransient) {
        // Wait 2s and retry once — covers the brief post-deploy boot window
        toast.message('Reconnecting…', { description: "Hold on, we're getting things ready." });
        await new Promise(r => setTimeout(r, 2000));
        result = await attemptLogin();
      }

      if (result.ok) {
        toast.success('Welcome back!');
        const params = new URLSearchParams(location.search);
        const nextPath = params.get('next') || '/dashboard';
        navigate(nextPath);
        return;
      }

      // Map error to a friendlier message
      let msg = result.detail;
      if (!msg) {
        if (result.status === 401) msg = 'Wrong email or password.';
        else if (result.isTransient) msg = "Server is starting up — please try again in a few seconds.";
        else msg = 'Login failed. Please try again.';
      }
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B0C10] text-[#F8F9FA] overflow-x-hidden" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Header */}
      <header className="fixed top-0 inset-x-0 z-50 backdrop-blur-xl bg-[#0B0C10]/80 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-baseline gap-2" data-testid="login-logo">
            <span className="text-xl font-semibold tracking-tight text-white" style={{ fontFamily: 'Playfair Display, serif' }}>RealProp</span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.25em] text-[#8A8D98]">Studio</span>
          </Link>
          <Link
            to="/signup"
            className="text-sm text-[#8A8D98] hover:text-white transition-colors inline-flex items-center gap-1"
            data-testid="header-signup-link"
          >
            Create account
            <ArrowUpRight className="h-3.5 w-3.5" />
          </Link>
        </div>
      </header>

      {/* Centered form on clean background */}
      <main className="min-h-screen flex items-center justify-center px-6 pt-28 pb-16">
        <motion.div
          initial="hidden"
          animate="show"
          variants={stagger}
          className="w-full max-w-md"
        >
          <motion.div variants={fadeUp} className="text-[10px] uppercase tracking-[0.3em] text-[#8A8D98] mb-8">
            <span style={{ color: BRASS }}>●</span>&nbsp;&nbsp;Sign in
          </motion.div>

          <motion.h1
            variants={fadeUp}
            className="text-5xl sm:text-6xl leading-[0.95] tracking-tight font-light"
            style={{ fontFamily: 'Outfit, sans-serif' }}
          >
            Welcome <span style={{ fontFamily: 'Playfair Display, serif', fontStyle: 'italic', color: BRASS }}>back.</span>
          </motion.h1>

          <motion.p variants={fadeUp} className="mt-5 text-sm text-[#8A8D98]">
            Sign in to your RealProp studio.
          </motion.p>

          <motion.div variants={fadeUp} className="mt-10">
            <SocialLoginButtons />
          </motion.div>

          <motion.div variants={fadeUp} className="my-8 flex items-center gap-3 text-[10px] uppercase tracking-[0.25em] text-[#8A8D98]">
            <span className="flex-1 h-px bg-white/10" />
            or with email
            <span className="flex-1 h-px bg-white/10" />
          </motion.div>

          <motion.form variants={fadeUp} onSubmit={handleSubmit} className="space-y-5" data-testid="login-form">
            <div>
              <label htmlFor="email" className="text-[10px] text-[#8A8D98] uppercase tracking-[0.25em]">Email</label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 w-full h-12 bg-transparent border-0 border-b border-white/15 text-white text-base placeholder-[#5b5e68] focus:outline-none focus:border-[#E3A869] transition-colors"
                data-testid="login-email-input"
              />
            </div>
            <div>
              <label htmlFor="password" className="text-[10px] text-[#8A8D98] uppercase tracking-[0.25em]">Password</label>
              <div className="relative">
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className="mt-2 w-full h-12 pr-10 bg-transparent border-0 border-b border-white/15 text-white text-base placeholder-[#5b5e68] focus:outline-none focus:border-[#E3A869] transition-colors"
                  data-testid="login-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-0 bottom-3 text-[#8A8D98] hover:text-white transition-colors"
                  data-testid="toggle-password-visibility"
                  aria-label="Toggle password visibility"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <div className="flex justify-end pt-1">
              <Link to="/forgot-password" className="text-xs text-[#8A8D98] hover:text-white transition-colors" data-testid="forgot-password-link">
                Forgot password?
              </Link>
            </div>

            <MagneticButton
              type="submit"
              disabled={loading}
              className="group w-full h-12 inline-flex items-center justify-center gap-2 rounded-full text-sm font-medium text-[#0B0C10] hover:opacity-90 disabled:opacity-50 transition-opacity mt-2"
              style={{ background: BRASS }}
              data-testid="login-submit-button"
            >
              {loading ? 'Signing in…' : (
                <>
                  Sign in
                  <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </MagneticButton>
          </motion.form>

          <motion.p variants={fadeUp} className="text-sm text-[#8A8D98] text-center mt-10">
            New here?{' '}
            <Link to="/signup" className="text-white hover:underline underline-offset-4 decoration-[#E3A869]" data-testid="signup-link">
              Create an account
            </Link>
          </motion.p>
        </motion.div>
      </main>
    </div>
  );
};

export default LoginPage;
